/**
 * Declaraciones de la operación, de solo lectura (Diego 2026-10-18: «el Ejecutivo de Cuenta no tiene cómo ver las
 * declaraciones; cualquier persona que esté trabajando en la operación las pueda ver»). Misma fuente que el resultado clave
 * Liquidación (resumen RIO): cada declaración de importación con sus casillas clave y tributos, la declaración andina del
 * valor, y los dos formularios en PDF (500 y 560). Se abre en ventana independiente desde la banda del DO.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-10-18
 */
import React, { useState } from "react";
import { BoxStatus, Button, PdfViewer, shootToast } from "codekit";
import { useGetResumenLiquidacionQuery } from "../../../../libs/redux/slices/Modimpxx/Nacional/Armdimxx/armdimxxApiSlice";
import { useGetPdfProformaDimMutation, usePreviewDeclaracionMutation } from "../../../../libs/redux/slices/Modimpxx/Nacional/Armdimxx/armdimxxWebSlice";
import { useGetPdfDavMutation } from "../../../../libs/redux/slices/Modimpxx/Nacional/Davdoxxx/davdoxxxWebSlice";
import { openGenioVisor } from "../../../../utils/genioVisor";
import "../../../Tablerox/riodo.css";
import "../../../Generals/rcrio.css";
import "./verdecla.css";

const usd = (v) => `USD ${Number(v ?? 0).toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cop = (v) => `$ ${Number(v ?? 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;
const n = (v, d = 2) => Number(v ?? 0).toLocaleString("es-CO", { minimumFractionDigits: d, maximumFractionDigits: d });
const urlDe = (r) => r?.data?.URLXXXXX || r?.URLXXXXX || r?.data?.data?.URLXXXXX || r?.data || "";
const CHIP = { ok: { background: "#DDF7E2", color: "#0D5120" }, azul: { background: "#EEF4F9", color: "#22629B" }, gris: { background: "#F4F6F8", color: "#5B6770" } };
const CHIP_PEND = { background: "#FBEFDE", color: "#8A5A12" };
const TEXTO_VERSION = { RADICADA: "radicada", ACEPTADA: "aceptada", LEVANTE: "con levante" };
const CHIP_DIAN = { SIN_EDI: CHIP.gris, POR_RADICAR: CHIP_PEND, RADICADA: CHIP.azul, CON_SOPORTES: CHIP.azul, ACEPTADA: CHIP.ok, INSPECCION: CHIP_PEND, LEVANTE: CHIP.ok };

const Dato = ({ etiqueta, valor, fuerte = false, rojo = false }) => (
  <div className="rcrio-leido">
    <span className="rcrio-leido-etiqueta">{etiqueta}</span>
    <span className="rcrio-leido-valor" style={{ ...(fuerte ? { fontWeight: 800 } : {}), ...(rojo ? { color: "#C42727", fontWeight: 800 } : {}) }}>{valor}</span>
  </div>
);

const Declara = ({ d }) => {
  const [verDescripcion, setVerDescripcion] = useState(false);
  return (
    <div className="rcrio-tarjeta" style={{ marginTop: 10 }}>
      <div className="rcrio-tarjeta-cabeza">
        <span className="rcrio-seccion" style={{ margin: 0 }}>Declaración {d.SUBIDXXX} · subpartida {d.SUBPARTIDA} · {d.MODALIDAD}</span>
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span className="rcrio-chip" style={CHIP_DIAN[d.DIAN?.ESTADO] ?? CHIP.gris} title="Estado de la declaración ante la DIAN">{d.DIAN?.TEXTO ?? "Sin EDI: no se ha radicado"}</span>
          <span className="rcrio-chip" style={CHIP.azul}>{d.ACUERDO && d.ACUERDO !== "XXX" ? `Acuerdo ${d.ACUERDO}` : "Sin acuerdo comercial"}</span>
        </span>
      </div>
      <div className="rcrio-nota">Proveedor {d.PROVEEDOR} · {d.INCOTERM} {d.MONEDA} · factura(s) {(d.FACTURAS ?? []).join(", ")} · país de origen {d.PAIS_ORIGEN}</div>
      <div className="rcrio-rejilla" style={{ marginTop: 6 }}>
        <Dato etiqueta="FOB (78)" valor={usd(d.FOB)} />
        <Dato etiqueta="Fletes (79)" valor={usd(d.FLETE)} />
        <Dato etiqueta="Seguros (80)" valor={usd(d.SEGURO)} />
        <Dato etiqueta="Valor en aduana (84)" valor={usd(d.VALOR_ADUANA)} fuerte />
        <Dato etiqueta="TRM (58)" valor={n(d.TRM)} />
        <Dato etiqueta="Base arancel (93)" valor={cop(d.BASE_COP)} />
        <Dato etiqueta={`Arancel ${n(d.ARANCEL_PCT)} % (94)`} valor={cop(d.ARANCEL_COP)} />
        <Dato etiqueta={`IVA ${n(d.IVA_PCT)} % (99)`} valor={cop(d.IVA_COP)} />
        <Dato etiqueta="Total tributos" valor={cop(d.TOTAL_COP)} fuerte />
        <Dato etiqueta="Registro de importación (85-88)" valor={d.REGISTRO?.TEXTO ?? "—"} fuerte={d.REGISTRO?.ESTADO === "OK"} rojo={d.REGISTRO?.ESTADO === "FALTA"} />
        <Dato etiqueta="Bultos · peso bruto / neto kg" valor={`${n(d.BULTOS, 0)} · ${n(d.PESO_BRUTO)} / ${n(d.PESO_NETO)}`} />
        <Dato etiqueta="Cantidad (77)" valor={`${n(d.CANTIDAD, 0)} ${d.UNIDAD ?? ""}`} />
        <Dato etiqueta="Ítems" valor={(d.ITEMS ?? []).map((i) => `${i.PROIDXXX} ×${n(i.CANTIDAD, 0)}`).join(" · ")} />
      </div>
      {/* Informacion aduanera transversal del DO, declaracion por declaracion (Diego 2026-10-20): numeros de radicado, aceptacion y levante a la vista */}
      <div className="rcrio-nota" style={{ marginTop: 8, fontWeight: 700 }}>Ante la DIAN</div>
      <div className="rcrio-rejilla">
        <Dato etiqueta="Radicado Siglo XXI" valor={d.DIAN?.RADICADO?.NUMERO ? `${d.DIAN.RADICADO.NUMERO} · ${d.DIAN.RADICADO.FECHA} ${d.DIAN.RADICADO.HORA}` : (d.DIAN?.EDI?.GENERADO ? "Por radicar (EDI generado)" : "Sin radicar")} fuerte={Boolean(d.DIAN?.RADICADO?.NUMERO)} rojo={!d.DIAN?.RADICADO?.NUMERO && !d.DIAN?.EDI?.GENERADO} />
        <Dato etiqueta="Documentos soporte" valor={d.DIAN?.SOPORTES ? "Diligenciados en Siglo XXI" : (d.DIAN?.RADICADO?.NUMERO ? "Pendientes (paso 2)" : "—")} />
        <Dato etiqueta="Aceptación (132/133)" valor={d.DIAN?.ACEPTACION?.NUMERO ? `${d.DIAN.ACEPTACION.NUMERO} · ${d.DIAN.ACEPTACION.FECHA} ${d.DIAN.ACEPTACION.HORA}${d.DIAN.ACEPTACION.RECIBO ? ` · recibo ${d.DIAN.ACEPTACION.RECIBO}` : ""}${d.DIAN.ACEPTACION.STICKER ? ` · sticker ${d.DIAN.ACEPTACION.STICKER}` : ""}` : "Pendiente"} fuerte={Boolean(d.DIAN?.ACEPTACION?.NUMERO)} />
        <Dato etiqueta="Levante (134/135)" valor={d.DIAN?.LEVANTE?.NUMERO || d.DIAN?.LEVANTE?.TIPO ? `${d.DIAN.LEVANTE.TIPO ? `${d.DIAN.LEVANTE.TIPO} ` : ""}${d.DIAN.LEVANTE.NUMERO} · ${d.DIAN.LEVANTE.FECHA} ${d.DIAN.LEVANTE.HORA}` : "Pendiente"} fuerte={Boolean(d.DIAN?.LEVANTE?.NUMERO)} />
        {/* Declaracion versionada (Diego 2026-10-20, validado con Legal): un solo documento por declaracion en Genio, que se reemplaza en cada paso */}
        <Dato etiqueta="Declaración en Genio" valor={d.DOCUMENTO?.IDARCHXX ? (
          <button type="button" className="rcrio-enlace" style={{ border: "none", background: "none", padding: 0, fontWeight: 800, color: "#2875B4", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit" }}
            onClick={() => openGenioVisor(`IDARCHXX=${d.DOCUMENTO.IDARCHXX}`)} title="Abrir el documento vigente en Genio">
            {`Declaración ${TEXTO_VERSION[d.DOCUMENTO.VERSION] ?? d.DOCUMENTO.VERSION} · abrir en Genio`}
          </button>
        ) : (d.DIAN?.RADICADO?.NUMERO ? "Se publica con la próxima sincronización de robots" : "Se publica al radicar")} />
      </div>
      <div className="rcrio-fila-accion" style={{ marginTop: 8 }}>
        <Button type="button" size="s" variant="secondary-light" onClick={() => setVerDescripcion((v) => !v)}>{verDescripcion ? "Ocultar descripción (91)" : "Ver descripción (91)"}</Button>
        <span className="rcrio-nota">Generada {d.GENERADA}</span>
      </div>
      {verDescripcion && <div className="rcrio-nota" style={{ marginTop: 6, whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 11.5 }}>{d.DESCRIPCION}</div>}
    </div>
  );
};

const Verdecla = ({ DOCNROXX, onVolver }) => {
  const { data: respuesta, isFetching, isError } = useGetResumenLiquidacionQuery({ DOCNROXX }, { skip: !DOCNROXX, refetchOnMountOrArgChange: true });
  //Formulario 500 nativo sobre la plantilla DIAN (Diego 2026-10-18: "este no es el formato de formulario 500"); la proforma de
  //SIACO queda como segunda opcion
  const [pdfForm500, { isLoading: isDim }] = usePreviewDeclaracionMutation();
  const [pdfProforma, { isLoading: isProforma }] = useGetPdfProformaDimMutation();
  const [pdfDav, { isLoading: isDav }] = useGetPdfDavMutation();
  const r = respuesta?.data ?? {};
  const declaraciones = r.DECLARACIONES ?? [];
  const totales = r.TOTALES ?? {};
  //El PDF se muestra en la misma pagina (visor de codekit): una segunda ventana desde una ventana emergente la frena el
  //bloqueador del navegador y el usuario no ve nada (Diego 2026-10-18: "no veo el boton para ver el PDF")
  const [visor, setVisor] = useState({ url: null, titulo: "", cargando: false, error: "" });

  const abrir = async (accion, nombre) => {
    setVisor({ url: null, titulo: nombre, cargando: true, error: "" });
    try {
      const respuesta = await accion(nombre === "formulario 500" ? { docId: DOCNROXX } : { DOCNROXX }).unwrap();
      //El formulario 500 llega como PDF (blob); los demas como enlace temporal
      const url = respuesta instanceof Blob ? URL.createObjectURL(respuesta) : urlDe(respuesta);
      if (!url) {
        setVisor({ url: null, titulo: nombre, cargando: false, error: `No llegó el enlace del ${nombre}.` });
        return;
      }
      setVisor({ url, titulo: nombre, cargando: false, error: "" });
      setTimeout(() => document.getElementById("verdecla-visor")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (error) {
      const detalle = error?.data?.errors?.exception ?? error?.data?.errors?.message ?? error?.error ?? (error?.status ? `respuesta ${error.status}` : "");
      setVisor({ url: null, titulo: nombre, cargando: false, error: `No se pudo generar el ${nombre}${detalle ? `: ${detalle}` : "."}` });
      shootToast({ variant: "error", text: `No se pudo generar el ${nombre}${detalle ? `: ${detalle}` : "."}`, autoHide: true, duration: 8000 });
    }
  };
  const handleDescargar = () => {
    if (visor.url) window.open(visor.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="riodo-escala">
      <div className="riodo vdc">
        <header className="rio-band">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {onVolver && <Button type="button" variant="secondary" size="m" icon="back-arrow" onClick={onVolver}>Volver</Button>}
            <div>
              <div className="rio-band-sub">Declaración de importación y declaración andina del valor · solo lectura</div>
              <div className="rio-band-title">Declaraciones · DO {DOCNROXX}{r.OPERACION?.CLINOMXX ? <span style={{ fontWeight: 600, opacity: 0.85 }}> · {r.OPERACION.CLINOMXX}</span> : null}</div>
            </div>
          </div>
          {declaraciones.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="button" size="s" variant="primary-bold" isLoading={isDim} onClick={() => abrir(pdfForm500, "formulario 500")}>Declaración de importación · Formulario 500 (PDF)</Button>
              <Button type="button" size="s" variant="secondary-bold" isLoading={isDav} disabled={!(r.DAV?.CANTIDAD > 0)} onClick={() => abrir(pdfDav, "formulario 560")}>Declaración andina del valor · Formulario 560 (PDF)</Button>
              <Button type="button" size="s" variant="secondary-light" isLoading={isProforma} onClick={() => abrir(pdfProforma, "proforma SIACO")}>Proforma SIACO (PDF)</Button>
            </div>
          )}
        </header>
        <div className="rcrio" style={{ padding: "8px 4px" }}>
          {isFetching && <BoxStatus variant="loading" text="Leyendo las declaraciones..." />}
          {isError && <BoxStatus variant="error" text="No se pudieron leer las declaraciones." />}
          {!isFetching && !isError && declaraciones.length === 0 && (
            <div className="rcrio-tarjeta"><div className="rcrio-nota">Esta operación todavía no está liquidada: las declaraciones aparecen aquí cuando el Especialista las genere.</div></div>
          )}
          {declaraciones.length > 0 && (
            <div className="rcrio-tarjeta">
              <div className="rcrio-tarjeta-cabeza">
                <span className="rcrio-seccion" style={{ margin: 0 }}>{declaraciones.length} declaración(es) de importación{r.DAV?.CANTIDAD ? ` · ${r.DAV.CANTIDAD} declaración(es) andina(s) del valor` : ""}</span>
                <span className="rcrio-chip" style={CHIP.ok}>{`Generadas ${declaraciones.map((x) => x.GENERADA).sort().slice(-1)[0] ?? ""}`}</span>
              </div>
              <div className="rcrio-rejilla">
                <Dato etiqueta="FOB + fletes + seguros (USD)" valor={`${n(totales.FOB)} + ${n(totales.FLETE)} + ${n(totales.SEGURO)}`} />
                <Dato etiqueta="Valor en aduana (USD)" valor={n(totales.VALOR_ADUANA)} fuerte />
                <Dato etiqueta="Arancel + IVA (COP)" valor={`${cop(totales.ARANCEL_COP)} + ${cop(totales.IVA_COP)}`} />
                <Dato etiqueta="Total tributos (COP)" valor={cop(totales.TOTAL_COP)} fuerte />
                <Dato etiqueta="TRM" valor={n(r.TRM?.VALOR)} />
                {r.DAV?.CANTIDAD > 0 && <Dato etiqueta="Formularios DAV" valor={(r.DAV.NUMEROS ?? []).join(" · ")} />}
                {r.DIAN?.TEXTO && <Dato etiqueta="Ante la DIAN" valor={r.DIAN.TEXTO} fuerte />}
              </div>
              <div className="rcrio-nota" style={{ marginTop: 6 }}>{r.PARTICION?.TEXTO}.</div>
            </div>
          )}
          {(visor.cargando || visor.url || visor.error) && (
            <div id="verdecla-visor" className="rcrio-tarjeta" style={{ marginTop: 10 }}>
              <div className="rcrio-tarjeta-cabeza">
                <span className="rcrio-seccion" style={{ margin: 0 }}>{visor.titulo ? `${visor.titulo.charAt(0).toUpperCase()}${visor.titulo.slice(1)}` : "PDF"}</span>
                <span className="rcrio-nota">Para guardarlo o imprimirlo usa «Descargar»: abre el PDF en una pestaña nueva.</span>
              </div>
              <PdfViewer
                src={visor.url}
                title={`${visor.titulo} · DO ${DOCNROXX}`}
                loading={visor.cargando}
                error={visor.error}
                onClose={() => setVisor({ url: null, titulo: "", cargando: false, error: "" })}
                onReload={() => abrir(visor.titulo === "formulario 560" ? pdfDav : visor.titulo === "proforma SIACO" ? pdfProforma : pdfForm500, visor.titulo)}
                onDownload={handleDescargar}
              />
            </div>
          )}
          {declaraciones.map((d) => <Declara key={d.SUBIDXXX} d={d} />)}
        </div>
      </div>
    </div>
  );
};

export default Verdecla;
